// Chat WebSocket Client for Web
// Manages a persistent WebSocket connection to the chat service for real-time events

const API_BASE_URL = 'https://api.familypal.com/familypal/v1/chat';

// Derive WS URL from API URL
const WS_BASE_URL = API_BASE_URL.replace(/^https?/, 'wss');

// ============================================================================
// WebSocket Message Types
// ============================================================================

export interface WSMessage {
  event: string;
  data?: any;
  seq?: number;
  seq_reply?: number;
  broadcast?: {
    chat_id?: string;
    user_id?: string;
    family_id?: string;
    omit_user?: string;
  };
}

// Server event names
export const WSEvents = {
  // Server → Client
  HELLO: 'hello',
  ERROR: 'error',
  MESSAGE_NEW: 'message_new',
  MESSAGE_EDITED: 'message_edited',
  MESSAGE_DELETED: 'message_deleted',
  TYPING_START: 'typing_start',
  TYPING_STOP: 'typing_stop',
  REACTION_ADDED: 'reaction_added',
  REACTION_REMOVED: 'reaction_removed',
  PARTICIPANT_JOINED: 'participant_joined',
  PARTICIPANT_LEFT: 'participant_left',
  READ_RECEIPT: 'read_receipt',
  CHAT_CREATED: 'chat_created',
  CHAT_UPDATED: 'chat_updated',
  RECONNECT_SUCCESS: 'reconnect_success',
  PONG: 'pong',
  PRESENCE_CHANGED: 'presence_changed',
  PRESENCE_BATCH: 'presence_batch',

  // Client → Server
  SUBSCRIBE: 'subscribe',
  UNSUBSCRIBE: 'unsubscribe',
  PING: 'ping',
  RECONNECT: 'reconnect',
  SET_PRESENCE: 'set_presence',
  QUERY_PRESENCE: 'query_presence',
} as const;

// Event emitter for web (replacement for React Native's DeviceEventEmitter)
type EventCallback = (data: any) => void;

class WebEventEmitter {
  private listeners: Map<string, Set<EventCallback>> = new Map();

  on(event: string, callback: EventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  off(event: string, callback: EventCallback): void {
    this.listeners.get(event)?.delete(callback);
  }

  emit(event: string, data?: any): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (err) {
        console.error(`Error in event listener for ${event}:`, err);
      }
    });
  }

  removeAllListeners(event?: string): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

export const chatEventEmitter = new WebEventEmitter();

// Chat event names (matching mobile constants)
export const ChatEvents = {
  USER_TYPING: 'chat_user_typing',
  USER_STOP_TYPING: 'chat_user_stop_typing',
  MESSAGE_SENT: 'chat_message_sent',
  MESSAGE_RECEIVED: 'chat_message_received',
  MESSAGE_UPDATED: 'chat_message_updated',
  MESSAGE_DELETED: 'chat_message_deleted',
  READ_RECEIPT_UPDATE: 'chat_read_receipt_update',
  CONNECTED: 'ws_connected',
  DISCONNECTED: 'ws_disconnected',
  CHAT_CREATED: 'ws_chat_created',
  CHAT_UPDATED: 'ws_chat_updated',
  REACTION_ADDED: 'ws_reaction_added',
  REACTION_REMOVED: 'ws_reaction_removed',
} as const;

// ============================================================================
// WebSocket Client
// ============================================================================

const PING_INTERVAL = 30_000; // 30 seconds
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Backoff delays
const MAX_RECONNECT_ATTEMPTS = 10;

class ChatWebSocketClient {
  private ws: WebSocket | null = null;
  private familyId: string | null = null;
  private userId: string | null = null;
  private sessionToken: string | null = null;
  private sessionId: string | null = null;
  private connectionId: string | null = null;
  private lastSeq = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private subscribedChatIds: Set<string> = new Set();
  private intentionalClose = false;
  private connecting = false;

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  setUserId(userId: string): void {
    this.userId = userId;
  }

  setSessionToken(token: string | null): void {
    // Note: Web uses cookie-based auth. Only set this if you have an actual auth token.
    // The session.id from Ory is NOT a valid auth token.
    this.sessionToken = token;
  }

  setSessionId(sessionId: string | null): void {
    // Set the Ory session ID for verification-based auth
    // Backend can verify this with Ory to authenticate the WebSocket connection
    this.sessionId = sessionId;
  }

  // Connect to the chat WebSocket
  async connect(familyId?: string): Promise<void> {
    if (this.connecting) return;
    if (this.isConnected && this.familyId === (familyId || null)) return;

    this.familyId = familyId || null;
    this.intentionalClose = false;
    this.connecting = true;

    try {
      await this.establishConnection();
    } finally {
      this.connecting = false;
    }
  }

  private async establishConnection(): Promise<void> {
    // Close existing connection
    this.cleanup();

    // Build WebSocket URL with authentication
    // Web uses cookie-based auth, but cookies may not work for cross-origin WS
    // Pass session token (if available from native flow) or session ID (for Ory verification)
    const params: string[] = [];
    if (this.familyId) {
      params.push(`family_id=${this.familyId}`);
    }
    if (this.sessionToken) {
      // Native/API flow token - preferred
      params.push(`token=${encodeURIComponent(this.sessionToken)}`);
    } else if (this.sessionId) {
      // Browser flow - pass session ID for backend to verify with Ory
      params.push(`session_id=${encodeURIComponent(this.sessionId)}`);
    }
    if (this.userId) {
      params.push(`user_id=${encodeURIComponent(this.userId)}`);
    }
    let wsUrl = `${WS_BASE_URL}/ws`;
    if (params.length > 0) {
      wsUrl += '?' + params.join('&');
    }
    console.log('ChatWebSocket: Connecting to', wsUrl.replace(/token=[^&]+/, 'token=***').replace(/session_id=[^&]+/, 'session_id=***'));

    return new Promise<void>((resolve, reject) => {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('ChatWebSocket: Connected');
        this.reconnectAttempt = 0;
        this.startPingTimer();
        chatEventEmitter.emit(ChatEvents.CONNECTED);

        // Send any pending subscriptions that were requested before connection was ready
        if (this.subscribedChatIds.size > 0) {
          console.log('ChatWebSocket: Sending pending subscriptions for', this.subscribedChatIds.size, 'chats');
          this.send({
            event: WSEvents.SUBSCRIBE,
            data: { chat_ids: Array.from(this.subscribedChatIds) },
          });
        }

        resolve();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        this.handleMessage(event.data);
      };

      this.ws.onclose = (event: CloseEvent) => {
        console.log(`ChatWebSocket: Closed (code=${event.code}, reason=${event.reason})`);
        this.stopPingTimer();
        chatEventEmitter.emit(ChatEvents.DISCONNECTED);

        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (event: Event) => {
        console.error('ChatWebSocket: Error', event);
        reject(new Error('WebSocket connection failed'));
      };
    });
  }

  // Disconnect and clean up
  disconnect(): void {
    this.intentionalClose = true;
    this.cleanup();
    this.subscribedChatIds.clear();
    this.connectionId = null;
    this.lastSeq = 0;
    this.familyId = null;
  }

  private cleanup(): void {
    this.stopPingTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
  }

  // ========================================================================
  // Reconnection
  // ========================================================================

  private scheduleReconnect(): void {
    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.error('ChatWebSocket: Max reconnect attempts reached');
      return;
    }

    const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)];
    console.log(`ChatWebSocket: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt + 1})`);

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectAttempt++;
      try {
        await this.establishConnection();

        // After reconnecting, try to recover missed messages
        if (this.connectionId && this.lastSeq > 0) {
          this.send({
            event: WSEvents.RECONNECT,
            data: {
              connection_id: this.connectionId,
              last_seq: this.lastSeq,
            },
          });
        }

        // Re-subscribe to all chats
        if (this.subscribedChatIds.size > 0) {
          this.subscribe(Array.from(this.subscribedChatIds));
        }
      } catch (err) {
        console.error('ChatWebSocket: Reconnection failed', err);
        this.scheduleReconnect();
      }
    }, delay);
  }

  // ========================================================================
  // Ping/Pong
  // ========================================================================

  private startPingTimer(): void {
    this.stopPingTimer();
    this.pingTimer = setInterval(() => {
      if (this.isConnected) {
        this.send({ event: WSEvents.PING });
      }
    }, PING_INTERVAL);
  }

  private stopPingTimer(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // ========================================================================
  // Send/Receive
  // ========================================================================

  private send(msg: WSMessage): void {
    if (!this.isConnected) {
      console.warn('ChatWebSocket: Cannot send, not connected');
      return;
    }
    console.log('ChatWebSocket: SEND', msg.event, JSON.stringify(msg.data));
    this.ws!.send(JSON.stringify(msg));
  }

  private handleMessage(rawData: string): void {
    let msg: WSMessage;
    try {
      msg = JSON.parse(rawData);
    } catch {
      console.error('ChatWebSocket: Failed to parse message', rawData);
      return;
    }

    // Log ALL incoming messages for debugging
    console.log('ChatWebSocket: RECV', msg.event, msg.data ? JSON.stringify(msg.data).substring(0, 200) : '(no data)');

    // Track sequence numbers for reconnection recovery
    if (msg.seq) {
      this.lastSeq = msg.seq;
    }

    switch (msg.event) {
      case WSEvents.HELLO:
        this.connectionId = msg.data?.connection_id;
        console.log(`ChatWebSocket: Hello (connId=${this.connectionId})`);
        break;

      case WSEvents.PONG:
        // Keep-alive acknowledged
        break;

      case WSEvents.MESSAGE_NEW: {
        const newMsgData = msg.data?.message || msg.data;
        chatEventEmitter.emit(ChatEvents.MESSAGE_RECEIVED, newMsgData);
        break;
      }

      case WSEvents.MESSAGE_EDITED: {
        const editedMsgData = msg.data?.message || msg.data;
        chatEventEmitter.emit(ChatEvents.MESSAGE_UPDATED, editedMsgData);
        break;
      }

      case WSEvents.MESSAGE_DELETED:
        chatEventEmitter.emit(ChatEvents.MESSAGE_DELETED, {
          threadId: msg.broadcast?.chat_id || msg.data?.chat_id,
          messageId: msg.data?.message_id || msg.data?.id,
        });
        break;

      case WSEvents.TYPING_START:
        chatEventEmitter.emit(ChatEvents.USER_TYPING, {
          chatId: msg.broadcast?.chat_id,
          userId: msg.data?.user_id || msg.broadcast?.user_id,
          displayName: msg.data?.display_name,
          now: Date.now(),
        });
        break;

      case WSEvents.TYPING_STOP:
        chatEventEmitter.emit(ChatEvents.USER_STOP_TYPING, {
          chatId: msg.broadcast?.chat_id,
          userId: msg.data?.user_id || msg.broadcast?.user_id,
          now: Date.now(),
        });
        break;

      case WSEvents.REACTION_ADDED:
        chatEventEmitter.emit(ChatEvents.REACTION_ADDED, msg.data);
        break;

      case WSEvents.REACTION_REMOVED:
        chatEventEmitter.emit(ChatEvents.REACTION_REMOVED, msg.data);
        break;

      case WSEvents.READ_RECEIPT:
        chatEventEmitter.emit(ChatEvents.READ_RECEIPT_UPDATE, {
          threadId: msg.broadcast?.chat_id,
          userId: msg.data?.user_id,
          messageId: msg.data?.message_id,
          timestamp: Date.now(),
        });
        break;

      case WSEvents.CHAT_CREATED: {
        const chatId = msg.data?.chat_id;
        if (chatId) {
          this.subscribe([chatId]);
        }
        chatEventEmitter.emit(ChatEvents.CHAT_CREATED, msg.data);
        break;
      }

      case WSEvents.CHAT_UPDATED:
        chatEventEmitter.emit(ChatEvents.CHAT_UPDATED, msg.data);
        break;

      case WSEvents.PRESENCE_CHANGED:
        // Presence updates - could be used for showing online status
        break;

      case WSEvents.PRESENCE_BATCH:
        // Batch presence response
        break;

      case WSEvents.RECONNECT_SUCCESS:
        console.log(`ChatWebSocket: Reconnect recovered ${msg.data?.recovered_count || 0} messages`);
        break;

      case WSEvents.ERROR:
        console.error('ChatWebSocket: Server error', msg.data);
        break;

      default:
        console.log(`ChatWebSocket: Unknown event: ${msg.event}`, msg.data);
    }
  }

  // ========================================================================
  // Subscriptions
  // ========================================================================

  subscribe(chatIds: string[]): void {
    console.log('ChatWebSocket: subscribe() called, chatIds=', chatIds, 'isConnected=', this.isConnected);
    chatIds.forEach((id) => this.subscribedChatIds.add(id));
    if (this.isConnected) {
      this.send({
        event: WSEvents.SUBSCRIBE,
        data: { chat_ids: chatIds },
      });
    } else {
      console.warn('ChatWebSocket: Not connected, subscription will be sent on connect');
    }
  }

  unsubscribe(chatIds: string[]): void {
    chatIds.forEach((id) => this.subscribedChatIds.delete(id));
    if (this.isConnected) {
      this.send({
        event: WSEvents.UNSUBSCRIBE,
        data: { chat_ids: chatIds },
      });
    }
  }

  // ========================================================================
  // Presence
  // ========================================================================

  setPresence(status: 'online' | 'away' | 'dnd'): void {
    this.send({
      event: WSEvents.SET_PRESENCE,
      data: { status },
    });
  }

  queryPresence(userIds: string[]): void {
    this.send({
      event: WSEvents.QUERY_PRESENCE,
      data: { user_ids: userIds },
    });
  }
}

// Export singleton
export const ChatWebSocket = new ChatWebSocketClient();
export default ChatWebSocket;
